
import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import PrimaryButton from '@/components/PrimaryButton';
import Toast from '@/components/Toast';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { profileService } from '@/services/profileService';

const INTEREST_OPTIONS = [
  'Abstinência',
  'Adultos 60+',
  'Álcool',
  'Ansiedade',
  'Apadrinhamento',
  'Autocompaixão',
  'Autoconhecimento',
  'Autocontrole',
  'Bariátrica',
  'Burnout',
  'Compras Compulsivas',
  'Comunicação',
  'Dependência Digital',
  'Depressão',
  'Drogas / Psicoativos',
  'Emoções',
  'Estresse',
  'Família',
  'Feminilidade',
  'Gatilhos',
  'Internação',
  'Jogos',
  'Juventude',
  'LGBT+',
  'Limites',
  'Lucidez',
  'Masculinidade',
  'Maternidade',
  'Neurodivergência',
  'Novos na Recuperação',
  'Outras Dependências',
  'Paternidade',
  'Pornografia',
  'Prevenção de Recaída',
  'Psiquiatria & Medicação',
  'Raiva',
  'Recomeços',
  'Rede de Apoio',
  'Redes Sociais',
  'Relacionamentos',
  'Ressaca',
  'Rotina de Sobriedade',
  'Sexo',
  'Sobriedade Longa',
  'Solidão',
  'Terapia',
  'Transtornos Alimentares',
  'Tratamento',
  'Vergonha & Culpa',
  'Vida Sexual Saudável',
];

export default function InterestsSettingsScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const { user, userProfile, updateProfile } = useAuth();
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  useEffect(() => {
    if (userProfile?.interests && Array.isArray(userProfile.interests)) {
      setSelectedInterests(userProfile.interests);
      console.log('📋 Loaded user interests from profile:', userProfile.interests);
    } else {
      console.log('📋 No interests found in profile, starting with empty selection');
      setSelectedInterests([]);
    }
  }, [userProfile?.interests]);

  const handleInterestToggle = (interest: string) => {
    setSelectedInterests(prev => {
      if (prev.includes(interest)) {
        return prev.filter(item => item !== interest);
      } else {
        return [...prev, interest];
      }
    });
  };

  const handleSave = async () => {
    if (selectedInterests.length < 3) {
      showToast(t('account.interests.errorMinimum'), 'error');
      return;
    }

    if (!user) {
      showToast(t('account.interests.errorUserNotFound'), 'error');
      return;
    }

    setLoading(true);

    try {
      const result = await profileService.updateInterests(selectedInterests);

      if (!result.success) {
        throw new Error(result.error || 'Failed to update interests');
      }

      // Update the context with the new interests
      const { error: contextError } = await updateProfile({ interests: selectedInterests });

      if (contextError) {
        console.warn('Context update warning:', contextError);
      }

      console.log('✅ Interests updated successfully:', selectedInterests);
      console.log('📊 Updated profile context with new interests');
      showToast(t('account.interests.successMessage'), 'success');
      
      // Navigate back after a short delay
      setTimeout(() => {
        router.back();
      }, 1500);

    } catch (error) {
      console.error('❌ Error updating interests:', error);
      showToast(t('account.interests.errorGeneric'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.back();
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const renderInterestPill = (interest: string) => {
    const isSelected = selectedInterests.includes(interest);
    
    return (
      <TouchableOpacity
        key={interest}
        style={[
          styles.pill,
          isSelected ? styles.pillSelected : styles.pillUnselected
        ]}
        onPress={() => handleInterestToggle(interest)}
      >
        <View style={styles.pillIcon}>
          {isSelected ? (
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path 
                d="M12.6673 4.2915L6.25065 10.7082L3.33398 7.7915" 
                stroke="#015480" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </Svg>
          ) : (
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path 
                d="M4 8H12" 
                stroke="#151515" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
              <Path 
                d="M8 4V12" 
                stroke="#151515" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </Svg>
          )}
        </View>
        <Text style={[
          styles.pillText,
          isSelected ? styles.pillTextSelected : styles.pillTextUnselected
        ]}>
          {interest}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader 
        title={t('account.interests.title')} 
        onBack={handleBack}
        backgroundColor="#ffffff"
      />

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Description Section */}
        <View style={styles.section}>
          <Text style={styles.title}>{t('account.interests.pageTitle')}</Text>
          <Text style={styles.subtitle}>
            {t('account.interests.subtitle')}
          </Text>
        </View>

        {/* Interests Section */}
        <View style={styles.interestsContainer}>
          {INTEREST_OPTIONS.map(renderInterestPill)}
        </View>
      </ScrollView>

      {/* Save Button */}
      <View style={styles.buttonSection}>
        <PrimaryButton
          title={loading ? t('account.interests.buttonLoading') : t('account.interests.button')}
          onPress={handleSave}
          disabled={loading || selectedInterests.length < 3}
        />
      </View>

      {/* Toast */}
      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    height: 56,
  },
  backButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  titleContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  headerTitle: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
  },
  placeholder: {
    width: 24,
    height: 24,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 24,
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 8,
    height: 134,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    textAlign: 'left',
  },
  subtitle: {
    color: '#757c85',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    textAlign: 'left',
  },
  interestsContainer: {
    padding: 24,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-start',
    flex: 1,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingLeft: 8,
    paddingRight: 12,
    borderRadius: 17,
    borderWidth: 1,
    gap: 4,
  },
  pillSelected: {
    backgroundColor: '#e6ebf0',
    borderColor: '#015480',
  },
  pillUnselected: {
    backgroundColor: '#ffffff',
    borderColor: '#dad8cf',
  },
  pillIcon: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pillText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16.8,
  },
  pillTextSelected: {
    color: '#015480',
  },
  pillTextUnselected: {
    color: '#151515',
  },
  buttonSection: {
    padding: 24,
    gap: 24,
  },
});
